import React, { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import UpgradeModal from "@/components/UpgradeModal";
import { Archive, Upload, Loader2, Camera, Send, Mail, MessageSquare, Check, Copy, Crown } from "lucide-react";

const Vault = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [gated, setGated] = useState(false);
  const [upgradeGate, setUpgradeGate] = useState(false);
  const fileRef = useRef(null);
  // Notifications Outbox — manual fallback until Twilio/SendGrid keys are configured
  const [outbox, setOutbox] = useState([]);
  const [outboxLive, setOutboxLive] = useState({ email_live: false, sms_live: false });
  const [copiedId, setCopiedId] = useState("");

  const loadOutbox = useCallback(async () => {
    try {
      const r = await api.get("/notifications/pending");
      setOutbox(r.data.items || []);
      setOutboxLive({ email_live: r.data.email_live, sms_live: r.data.sms_live });
    } catch (e) { /* premium-gated features elsewhere may 403; outbox itself never should, but stay quiet */ }
  }, []);

  const copyNotification = (n) => {
    navigator.clipboard?.writeText(n.body);
    setCopiedId(n.id);
    setTimeout(() => setCopiedId(""), 2000);
  };

  const markSent = async (id) => {
    await api.post(`/notifications/pending/${id}/mark-sent`);
    await loadOutbox();
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get("/vault");
      setItems(res.data);
      setGated(false);
    } catch (err) {
      if (err?.response?.status === 403) setGated(true);
      else console.error("Vault load failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); loadOutbox(); }, [load, loadOutbox]);

  const onScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result.split(",")[1]);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const recipient = window.prompt("Who is this card for / from?", "Keepsake") || "Keepsake";
      const occasion = window.prompt("Occasion?", "Memory") || "Memory";
      const yearStr = window.prompt("Year?", String(new Date().getFullYear()));
      await api.post("/vault/scan", {
        image_base64: b64, recipient_name: recipient, occasion,
        year: yearStr ? parseInt(yearStr, 10) : undefined,
      });
      await load();
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // group by year desc, then recipient
  const byYear = {};
  for (const it of items) {
    (byYear[it.year] = byYear[it.year] || []).push(it);
  }
  const years = Object.keys(byYear).sort((a, b) => b.localeCompare(a));

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl mb-1 flex items-center gap-3">The Vault <Archive className="w-7 h-7 text-primary" /></h1>
          <p className="text-muted-foreground">Every card sent or scanned — a living timeline of your thoughtfulness.</p>
        </div>
        <button data-testid="scan-card-btn" onClick={() => fileRef.current?.click()} disabled={scanning}
          className="flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white rounded-2xl px-5 py-2.5 hover:opacity-90 disabled:opacity-60">
          {scanning ? <><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</> : <><Camera className="w-4 h-4" /> Scan a Card</>}
        </button>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onScan} data-testid="scan-input" />
      </div>

      {/* Notifications Outbox — manual fallback until Twilio/SendGrid keys are configured */}
      {outbox.length > 0 && (
        <div className="surface rounded-2xl p-4 mb-8" data-testid="notifications-outbox">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-medium"><Send className="w-4 h-4 gold-text" /> Notifications Outbox</div>
            <span className="text-[11px] text-muted-foreground">{outboxLive.email_live ? "" : "Email not connected — "}{outboxLive.sms_live ? "" : "SMS not connected"} · send these yourself for now</span>
          </div>
          <div className="space-y-2">
            {outbox.map((n) => (
              <div key={n.id} className="bg-muted/30 rounded-xl p-3 flex items-start gap-3" data-testid="outbox-item">
                <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                  {n.channel === "email" ? <Mail className="w-4 h-4 text-primary" /> : <MessageSquare className="w-4 h-4 text-primary" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground">To: {n.to}</div>
                  <p className="text-sm line-clamp-2">{n.body}</p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button onClick={() => copyNotification(n)} className="text-xs flex items-center gap-1 text-primary hover:underline">
                    {copiedId === n.id ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                  <button onClick={() => markSent(n.id)} className="text-xs text-muted-foreground hover:text-secondary">Mark sent</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-24 text-muted-foreground">Opening the vault…</div>
      ) : gated ? (
        <div className="surface rounded-[2rem] p-16 text-center" data-testid="vault-locked">
          <Crown className="w-12 h-12 mx-auto mb-4 gold-text" />
          <h3 className="font-serif text-3xl mb-2">A premium privilege</h3>
          <p className="text-muted-foreground mb-6">The Legacy Vault — your full gifting archive, organized by year — unlocks permanently with any Credit Pack.</p>
          <button onClick={() => setUpgradeGate(true)} data-testid="vault-upgrade-cta" className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white px-6 py-3 rounded-2xl hover:opacity-90"><Crown className="w-4 h-4" /> Buy a Credit Pack</button>
        </div>
      ) : items.length === 0 ? (
        <div className="surface rounded-[2rem] p-16 text-center">
          <Archive className="w-12 h-12 mx-auto mb-4 text-primary/60" />
          <h3 className="font-serif text-2xl mb-2">Your vault is waiting</h3>
          <p className="text-muted-foreground mb-6">Cards you send appear here automatically. You can also scan a physical card to preserve it.</p>
          <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white px-6 py-3 rounded-2xl hover:opacity-90">
            <Upload className="w-4 h-4" /> Scan a Card
          </button>
        </div>
      ) : (
        <div className="space-y-12" data-testid="vault-timeline">
          {years.map((year) => (
            <section key={year} data-testid="vault-year">
              <div className="flex items-center gap-4 mb-5">
                <h2 className="font-serif text-3xl gold-text">{year}</h2>
                <div className="flex-1 h-px bg-border" />
                <span className="text-sm text-muted-foreground">{byYear[year].length} keepsake{byYear[year].length === 1 ? "" : "s"}</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger">
                {byYear[year].map((c) => (
                  <div key={c.id} className="surface rounded-[1.5rem] overflow-hidden card-soft" data-testid="vault-card">
                    <div className="h-40 overflow-hidden">
                      <img src={c.image_thumb_base64 ? `data:image/jpeg;base64,${c.image_thumb_base64}` : `data:image/png;base64,${c.image_base64}`} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">{c.occasion}</span>
                        {c.source === "scanned" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">Scanned</span>}
                      </div>
                      <div className="font-serif text-lg">{c.loved_one_name}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      <UpgradeModal open={upgradeGate} onClose={() => setUpgradeGate(false)} tier="credit_pack" feature="The Legacy Vault" />
    </AppShell>
  );
};

export default Vault;
